<template>
  <div>
    <div class="my-8 mt-2 flex lg:inline-flex justify-between items-center">
      <h1 class="title text-md lg:text-2xl m-0">
        <span>Select a Meeting</span>
      </h1>
      <button
        v-if="user.isAdmin"
        type="button"
        class="button lg:ml-4"
        @click="showAddMeeting"
      >
        <span class="icon"><i class="fas fa-plus"></i></span>
        <span>Add New</span>
      </button>
      <button
        v-if="user.isAdmin"
        type="button"
        class="button lg:ml-4"
        @click="toggleShowArchived()"
      >
        <span class="icon"><i class="fas fa-folder-open"></i></span>
        <span
          >{{
            showArchived ? 'Hide Archived Meetings' : 'Show Archived Meetings'
          }}
        </span>
      </button>
    </div>

    <div class="columns mb-8">
      <div class="column">
        <div v-if="!user.username">
          You are currently not logged in.
        </div>
        <div
          v-else-if="
            !user.isAdmin &&
              !upcomingMeetings.length &&
              !Object.keys(user.meetings).length
          "
        >
          There are currently no meetings available.
        </div>
        <div v-else class="box">
          <table class="table is-fullwidth table-with-data-labels">
            <thead>
              <tr>
                <th scope="col">Meeting Name</th>
                <th scope="col">Dates</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="meeting in meetingsList" :key="meeting.id">
                <td data-label="Name">
                  <span v-if="user.isAdmin"
                    ><router-link
                      :to="{
                        name: 'MeetingDashboard',
                        params: { id: meeting.id }
                      }"
                      >{{ meeting.name }}</router-link
                    >
                    (Maxx ID: {{ meeting.maxxId }})</span
                  >
                  <router-link
                    v-else
                    :to="{ name: 'UserSuites', params: { id: meeting.id } }"
                    >{{ meeting.name }}</router-link
                  >
                </td>
                <td
                  data-label="Dates"
                  v-text="
                    `${formatTime(meeting.start_date)} - ${formatTime(
                      meeting.end_date
                    )}`
                  "
                ></td>
                <td data-label="Status">
                  <span
                    class="tag is-uppercase"
                    :class="phases[meeting.phase_index].style"
                    v-text="phases[meeting.phase_index].text"
                  ></span>
                  <span
                    class="tag is-uppercase is-black"
                    v-if="meeting.is_archived"
                  >
                    Archived
                  </span>
                </td>
              </tr>
              <tr v-for="meeting in upcomingMeetings" :key="meeting.id">
                <td data-label="Name">
                  {{ meeting.name }}
                </td>
                <td
                  data-label="Dates"
                  v-text="
                    `${formatTime(meeting.start_date)} - ${formatTime(
                      meeting.end_date
                    )}`
                  "
                ></td>
                <td data-label="Status">
                  <span
                    class="tag is-uppercase"
                    v-text="
                      `Scheduling begins ${formatTime(meeting.scheduler_date)}.`
                    "
                  ></span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <b-modal
      :active.sync="showingAddMeeting"
      trap-focus
      :width="700"
      @close="hideAddMeeting"
    >
      <form @submit.prevent="hideAddMeeting">
        <header class="modal-card-head">
          <p class="modal-card-title">Add New Meeting</p>
        </header>
        <section class="modal-card-body pb-8">
          <div class="field">
            <label class="checkbox">
              <input type="checkbox" v-model="selectedShowMaxxDupes" /> Show
              duplicate Maxx meetings
            </label>
          </div>
          <div class="field">
            <label class="label"
              >Select Meeting from Maxx to Link Registrations</label
            >
            <div class="control has-icons-left">
              <div class="select">
                <select
                  v-model="selectedMeeting"
                  :class="{ 'is-loading': meetingsAreLoading }"
                  :disabled="meetingsAreLoading"
                >
                  <option disabled value="">Select one...</option>
                  <option
                    v-for="meeting in availableMeetings"
                    :value="meeting"
                    :key="meeting.id"
                    >{{ meeting.name }}</option
                  >
                </select>
              </div>
              <span class="icon is-left"><i class="fas fa-calendar"></i></span>
            </div>
          </div>
          <div class="field">
            <input type="checkbox" v-model="selectedCopy" />
            <span @click="selectedCopy = !selectedCopy"
              >Copy an Existing Appointment Scheduler Meeting</span
            >
          </div>
          <div class="field" v-if="selectedCopy">
            <div>
              <div v-if="selectedCopy" class="select">
                <select v-model="meetingToCopyId">
                  <option disabled value="">Select one...</option>
                  <option
                    v-for="meeting in meetings"
                    :value="meeting.id"
                    :key="meeting.id"
                  >
                    {{ meeting.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div v-if="Object.keys(timeslotsForCopy).length > 0" class="field">
            <div class="label">Enter Dates for New Meeting</div>
            <div class="control">
              <div
                v-for="(slot, key, index) in timeslotsForCopy"
                :key="`slotindex${index}`"
              >
                <div class="field is-grouped mb-4">
                  <div class="label mr-4">Day {{ index + 1 }}:</div>
                  <div class="control">
                    <input
                      v-model="copyDates[index]"
                      type="date"
                      class="input"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="field">
            <label class="label">Display Name</label>
            <input type="text" class="input" v-model="newMeetingName" />
            <p class="help">
              The display name for the meeting in the scheduler. If left blank,
              will be the same as the Maxx meeting name.
            </p>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-success" @click="addMeeting">
            Add To Scheduler
          </button>
          <button type="button" class="button is-text" @click="hideAddMeeting">
            Cancel
          </button>
        </footer>
      </form>
    </b-modal>
  </div>
</template>

<script>
import swal from 'sweetalert';
import maxxAPI from '@/services/maxxapi';
import { phases } from '@/services/meeting';
import { mapGetters, mapState, mapActions } from 'vuex';
import { firestoreTimestampToDate } from '@/services/utils';
import dbhelpers from '@/services/dbhelpers';
import { db } from '@/services/firebase';

export default {
  data() {
    return {
      companies: [],
      meetings: [],
      upcomingMeetings: [],
      availableMeetings: [],
      selectedMeeting: '',
      phases: phases,
      showingAddMeeting: false,
      selectedCopy: false,
      selectedShowMaxxDupes: false,
      meetingsAreLoading: false,
      meetingToCopyId: '',
      newMeetingName: '',
      copyDates: []
    };
  },
  computed: {
    ...mapGetters(['user']),
    ...mapState(['showArchived']),
    timeslotsForCopy: function() {
      if (this.meetingToCopyId !== '') {
        let originalMeeting = this.meetings.filter(meeting => {
          return meeting.id === this.meetingToCopyId;
        })[0];
        if (originalMeeting.appointment_slots) {
          return originalMeeting.appointment_slots;
        }
      }
      return [];
    },
    meetingsList() {
      return this.meetings
        .filter(meeting => {
          return (
            (this.user.isAdmin &&
              (this.showArchived || !meeting.is_archived)) ||
            this.user.meetings[meeting.id]
          );
        })
        .sort((a, b) => {
          return a.start_date.seconds > b.start_date.seconds ? 1 : -1;
        })
        .sort((a, b) => {
          if (a.is_archived && !b.is_archived) {
            return 1;
          }

          if (b.is_archived && !a.is_archived) {
            return -1;
          }

          return 0;
        });
    }
  },
  watch: {
    selectedShowMaxxDupes(newVal, oldVal) {
      if (this.showingAddMeeting) {
        this.populateAvailableMeetings(newVal);
      }
    },
    selectedCopy(newVal, oldVal) {
      if (!newVal) {
        this.meetingToCopyId = '';
      }
    }
  },
  methods: {
    ...mapActions(['toggleShowArchived']),
    hideAddMeeting() {
      this.selectedMeeting = '';
      this.showingAddMeeting = false;
      this.selectedCopy = false;
      this.selectedShowMaxxDupes = false;
      this.meetingsAreLoading = false;
    },
    async populateAvailableMeetings(allowDupes) {
      this.meetingsAreLoading = true;
      const meetings = await maxxAPI.getMeetings();

      this.availableMeetings = allowDupes
        ? meetings
        : meetings.filter(apiMeeting => {
            return !this.meetings.find(
              meeting => meeting.maxxId === apiMeeting.id
            );
          });
      this.meetingsAreLoading = false;
    },
    async showAddMeeting() {
      await this.populateAvailableMeetings();
      this.showingAddMeeting = true;
    },
    addMeeting(e) {
      const name = this.newMeetingName
        ? this.newMeetingName
        : this.selectedMeeting.name;

      let toAdd = {
        name,
        maxxId: this.selectedMeeting.id,
        phase_index: 0,
        start_date: new Date(this.selectedMeeting.start_date),
        end_date: new Date(this.selectedMeeting.end_date),
        location: this.selectedMeeting.location,
        timezone: this.selectedMeeting.timezone
      };

      // flow for copy meeting
      let isCopying = this.selectedCopy;
      if (isCopying) {
        // don't submit if "meeting to copy" isn't selected
        if (this.meetingToCopyId === '') {
          e.preventDefault();
          return false;
        }
        let originalMeeting = this.meetings.filter(meeting => {
          return meeting.id === this.meetingToCopyId;
        })[0];
        let appointmentSlots = {};

        if (originalMeeting.appointment_slots) {
          let originalMeetingDates = Object.keys(
            originalMeeting.appointment_slots
          );

          // don't submit if meeting dates aren't set
          if (this.copyDates.length < originalMeetingDates.length) {
            e.preventDefault();
            return false;
          }
          this.copyDates.forEach((date, index) => {
            appointmentSlots[date] =
              originalMeeting.appointment_slots[originalMeetingDates[index]];
          });
        }

        toAdd = {
          ...toAdd,
          appointment_slots: appointmentSlots
        };
      }
      let originalMeetingRef = null;
      let originalUsersRef = null;
      let originalSuitesRef = null;
      let usersGood = {};
      let usersBad = [];
      if (this.meetingToCopyId) {
        originalMeetingRef = db
          .collection('meetings')
          .doc(this.meetingToCopyId);
        originalUsersRef = originalMeetingRef.collection('users');
        originalSuitesRef = originalMeetingRef.collection('suites');
      }
      let errorMessage = '';

      Promise.resolve()
        .then(() => {
          if (isCopying) {
            // if we are copying, make a call to Maxx to update users
            let users = {};
            return originalUsersRef
              .get()
              .then(originalUsersSnapshot => {
                originalUsersSnapshot.forEach(originalUser => {
                  users[originalUser.id] = originalUser.data();
                });
                return maxxAPI.getUpdatedUsers(users);
              })
              .catch(error => {
                errorMessage = error.message;
                return Promise.reject(new Error(errorMessage));
              })
              .then(updatedUsers => {
                usersGood = updatedUsers.good;
                usersBad = updatedUsers.missing;
              });
          }
        })
        .then(() => {
          return this.$firestoreRefs.meetings.add(toAdd);
        })
        .then(docRef => {
          if (isCopying) {
            let batch = db.batch();
            let newUsersRef = docRef.collection('users');
            let newSuitesRef = docRef.collection('suites');
            // iterate through good users and update their name and email as necessary
            // bad users who are no longer part of the associated company or not in maxx db get removed
            return Promise.resolve()
              .then(() => {
                let userKeys = Object.keys(usersGood);
                if (userKeys) {
                  userKeys.forEach(userId => {
                    batch.set(newUsersRef.doc(userId), usersGood[userId]);
                  });
                  return batch.commit();
                }
              })
              .then(() => {
                return originalSuitesRef.get();
              })
              .then(originalSuitesSnapshot => {
                if (originalSuitesSnapshot) {
                  batch = db.batch();
                  let activityArray = [];
                  originalSuitesSnapshot.forEach(originalSuite => {
                    let suiteData = originalSuite.data();
                    let newContacts = [];
                    suiteData.appointment_slots = {};
                    if (suiteData && suiteData.contacts) {
                      // clean out bad contacts and update good contacts as necessary
                      suiteData.contacts.forEach(contact => {
                        if (!usersBad.includes(contact.username)) {
                          let newContact = contact;
                          newContact['name'] = usersGood[contact.username].name;
                          newContact['email'] =
                            usersGood[contact.username].email;
                          newContacts.push(newContact);
                          if (suiteData && suiteData.company) {
                            activityArray.push({
                              meeting_id: toAdd.maxxId,
                              company_id: suiteData.company.maxxId,
                              suite_name: suiteData.name,
                              added_or_removed: 'added'
                            });
                          }
                        }
                      });
                    }
                    suiteData.contacts = newContacts;
                    batch.set(newSuitesRef.doc(originalSuite.id), suiteData);
                  });
                  maxxAPI.createBatchActivity(activityArray);
                  return batch.commit();
                }
              });
          }
        })
        .then(() => {
          this.showingAddMeeting = false;
          this.selectedMeeting = '';
          this.selectedCopy = false;
          this.meetingToCopyId = '';
          this.copyDates = [];
          let text = '';
          if (usersBad && isCopying) {
            text = `The following users have been removed: ${usersBad.join(
              ', '
            )}`;
          }
          swal({
            title: `Meeting ${isCopying ? 'Copied' : 'Created'} Successfully`,
            text,
            icon: 'success'
          });
        })
        .catch(error => {
          console.log(error);
          swal({
            title: `Error ${isCopying ? 'Copying' : 'Creating'} Meeting`,
            text: errorMessage,
            icon: 'error'
          });
        });
    },
    formatTime: firestoreTimestampToDate
  },
  firestore() {
    return {
      meetings: dbhelpers.getUserMeetings(),
      upcomingMeetings: dbhelpers.getUpcomingMeetings()
    };
  }
};
</script>



// WEBPACK FOOTER //
// src/components/MeetingsList.vue